import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../services/api";
import { extractObject, normalizeNumericFields } from "../utils/apiData";
import {
  formatChartDate,
  formatCurrency,
  formatDateForApi,
  formatInteger,
} from "../utils/formatters";

const severityVariant = {
  high: "danger",
  medium: "warning",
  low: "secondary",
};

const riskVariant = (score) => {
  if (score >= 70) return "danger";
  if (score >= 40) return "warning";
  return "success";
};

const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number >= 0 ? "+" : ""}${number.toFixed(1)}%`;
};

const FraudDetection = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    summary: {},
    monthlyComparison: [],
    alerts: [],
    methodology: [],
  });

  const fetchFraudIndicators = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.getFraudDetection({
        start_date: formatDateForApi(filters.startDate),
        end_date: formatDateForApi(filters.endDate),
        shop_id: filters.shopId || undefined,
        analysis_months: 6,
      });

      const payload = extractObject(response);
      const summary = payload.summary || {};

      setData({
        summary: {
          risk_score: Number(summary.risk_score) || 0,
          total_alerts: Number(summary.total_alerts) || 0,
          high_risk_alerts: Number(summary.high_risk_alerts) || 0,
          medium_risk_alerts: Number(summary.medium_risk_alerts) || 0,
          sellers_flagged: Number(summary.sellers_flagged) || 0,
          suspicious_value: Number(summary.suspicious_value) || 0,
          invoices_analysed: Number(summary.invoices_analysed) || 0,
        },
        monthlyComparison: normalizeNumericFields(
          Array.isArray(payload.monthly_comparison)
            ? payload.monthly_comparison
            : [],
          [
            "revenue",
            "invoice_count",
            "avg_order_value",
            "avg_discount",
            "after_hours_invoices",
            "after_hours_ratio",
            "revenue_change_pct",
            "invoice_change_pct",
            "discount_change_pct",
            "avg_order_change_pct",
            "risk_score",
          ],
        ),
        alerts: normalizeNumericFields(
          Array.isArray(payload.alerts) ? payload.alerts : [],
          ["amount", "metric", "baseline", "variance_pct"],
        ),
        methodology: Array.isArray(payload.methodology)
          ? payload.methodology
          : [],
      });
    } catch (requestError) {
      setError(
        requestError.message || "Failed to calculate fraud-risk indicators.",
      );
    } finally {
      setLoading(false);
    }
  }, [filters.endDate, filters.shopId, filters.startDate]);

  useEffect(() => {
    fetchFraudIndicators();
  }, [fetchFraudIndicators]);

  const sortedAlerts = useMemo(() => {
    const order = { high: 3, medium: 2, low: 1 };
    return [...data.alerts].sort(
      (left, right) =>
        (order[right.severity] || 0) - (order[left.severity] || 0),
    );
  }, [data.alerts]);

  if (loading) {
    return (
      <div className="content-loader">
        <Spinner animation="border" variant="danger" />
        <span>Checking transactions for anomalies…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Fraud-risk analysis could not load</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchFraudIndicators}>
          Retry
        </Button>
      </Alert>
    );
  }

  const { summary } = data;

  return (
    <div>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Anomaly monitoring</p>
          <h1>Fraud Risk Detection</h1>
        </div>
        <Button variant="outline-dark" onClick={fetchFraudIndicators}>
          Refresh Analysis
        </Button>
      </div>

      <Alert variant="info">
        These are automated risk indicators, not proof of fraud. Review the
        original invoice, seller activity, discounts, and business context
        before taking action.
      </Alert>

      <Row className="mb-4 g-3">
        <Col lg={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Risk Score</div>
              <div className="kpi-value">{summary.risk_score}/100</div>
              <ProgressBar
                now={summary.risk_score}
                variant={riskVariant(summary.risk_score)}
                aria-label="Fraud risk score"
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Detected Alerts</div>
              <div className="kpi-value">
                {formatInteger(summary.total_alerts)}
              </div>
              <div className="kpi-sub">
                {formatInteger(summary.high_risk_alerts)} high risk
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Suspicious Value</div>
              <div className="kpi-value">
                {formatCurrency(summary.suspicious_value)}
              </div>
              <div className="kpi-sub">
                From flagged invoice-level anomalies
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Sellers Flagged</div>
              <div className="kpi-value">
                {formatInteger(summary.sellers_flagged)}
              </div>
              <div className="kpi-sub">
                {formatInteger(summary.invoices_analysed)} invoices analysed
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header>Monthly Revenue and Risk Score</Card.Header>
            <Card.Body>
              {data.monthlyComparison.length ? (
                <ResponsiveContainer width="100%" height={330}>
                  <ComposedChart data={data.monthlyComparison}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tickFormatter={(value) =>
                        formatChartDate(`${value}-01`, "MMM yyyy")
                      }
                    />
                    <YAxis yAxisId="money" />
                    <YAxis
                      yAxisId="risk"
                      orientation="right"
                      domain={[0, 100]}
                    />
                    <Tooltip
                      labelFormatter={(value) =>
                        formatChartDate(`${value}-01`, "MMMM yyyy")
                      }
                      formatter={(value, name) => {
                        if (name === "Revenue") return formatCurrency(value);
                        if (name === "Risk Score") return `${value}/100`;
                        return value;
                      }}
                    />
                    <Legend />
                    <Bar
                      yAxisId="money"
                      dataKey="revenue"
                      fill="#c8102e"
                      name="Revenue"
                    />
                    <Line
                      yAxisId="risk"
                      type="monotone"
                      dataKey="risk_score"
                      stroke="#202020"
                      strokeWidth={3}
                      name="Risk Score"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-chart">
                  No monthly comparison data is available.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>Latest Month Comparison</Card.Header>
            <Card.Body>
              {data.monthlyComparison.length ? (
                (() => {
                  const latest =
                    data.monthlyComparison[data.monthlyComparison.length - 1];
                  return (
                    <Table responsive borderless className="mb-0">
                      <tbody>
                        <tr>
                          <th>Revenue change</th>
                          <td className="text-end">
                            {formatPercent(latest.revenue_change_pct)}
                          </td>
                        </tr>
                        <tr>
                          <th>Invoice change</th>
                          <td className="text-end">
                            {formatPercent(latest.invoice_change_pct)}
                          </td>
                        </tr>
                        <tr>
                          <th>Average order change</th>
                          <td className="text-end">
                            {formatPercent(latest.avg_order_change_pct)}
                          </td>
                        </tr>
                        <tr>
                          <th>Discount change</th>
                          <td className="text-end">
                            {formatPercent(latest.discount_change_pct)}
                          </td>
                        </tr>
                        <tr>
                          <th>After-hours invoices</th>
                          <td className="text-end">
                            {formatInteger(latest.after_hours_invoices)} (
                            {Number(latest.after_hours_ratio).toFixed(1)}%)
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  );
                })()
              ) : (
                <div className="empty-search-result">
                  Select a wider period to compare monthly activity.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>Risk Alerts</Card.Header>
        <Card.Body className="p-0">
          {sortedAlerts.length ? (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Alert</th>
                  <th>Seller</th>
                  <th>Shop</th>
                  <th>Date</th>
                  <th className="text-end">Value</th>
                  <th className="text-end">Variance</th>
                </tr>
              </thead>
              <tbody>
                {sortedAlerts.map((alert) => (
                  <tr key={alert.id}>
                    <td>
                      <Badge bg={severityVariant[alert.severity] || "secondary"}>
                        {String(alert.severity || "unknown").toUpperCase()}
                      </Badge>
                    </td>
                    <td style={{ minWidth: 280 }}>
                      <strong>{alert.title}</strong>
                      <div className="small text-muted">
                        {alert.description}
                      </div>
                      {alert.invoice_id && (
                        <div className="small">Invoice: {alert.invoice_id}</div>
                      )}
                    </td>
                    <td>{alert.seller || "—"}</td>
                    <td>{alert.shop_id || "—"}</td>
                    <td>
                      {alert.event_date
                        ? formatChartDate(alert.event_date, "MMM dd, yyyy")
                        : "—"}
                    </td>
                    <td className="text-end">
                      {formatCurrency(alert.amount)}
                    </td>
                    <td className="text-end">
                      {formatPercent(alert.variance_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="empty-chart">
              No anomalies matched the current detection rules.
            </div>
          )}
        </Card.Body>
      </Card>

      {data.methodology.length > 0 && (
        <Card>
          <Card.Header>Detection Rules</Card.Header>
          <Card.Body>
            <ul className="mb-0">
              {data.methodology.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default FraudDetection;
